/**
 * Controllo della fatturazione dei consulenti.
 *
 * Confronta, per ogni offerta accettata, quanto vale l'incarico con quanto il
 * consulente ha effettivamente fatturato. Serve a intercettare gli sforamenti
 * PRIMA di pagare, non a consuntivo.
 *
 * Lo sforamento si misura sulle sole FATTURE; il consuntivo è la previsione
 * che lo anticipa, e produce un avviso più tenue.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { offerteConsulentiApi, consulentiApi } from '../api';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

// Colore + icona + parola: il significato non è mai affidato al solo colore
const SEMAFORO = {
  ok:                   { riga: '',                 testo: 'text-gray-600',   icona: '',  label: '' },
  attenzione:           { riga: 'bg-amber-50/70',   testo: 'text-amber-700',  icona: '▲', label: 'Quasi esaurito' },
  previsto_sforamento:  { riga: 'bg-amber-50/40',   testo: 'text-amber-700',  icona: '▲', label: 'Sforamento previsto' },
  sforato:              { riga: 'bg-red-50/70',     testo: 'text-red-700',    icona: '■', label: 'SFORATO' },
};

export default function ControlloConsulenti() {
  const [dati, setDati] = useState(null);
  const [consulenti, setConsulenti] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [soloProblemi, setSoloProblemi] = useState(false);

  const carica = () => offerteConsulentiApi.controllo({ consulente_id: filtro || undefined })
    .then(setDati).catch(console.error);
  useEffect(() => { carica(); }, [filtro]);
  useEffect(() => { consulentiApi.list().then(setConsulenti).catch(() => {}); }, []);

  if (!dati) return <div className="text-sm text-gray-400 py-8 text-center">Caricamento…</div>;

  const righe = soloProblemi
    ? dati.righe.filter(r => r.stato_controllo !== 'ok')
    : dati.righe;
  const t = dati.totali;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <select className="input py-1 text-sm max-w-[240px]" value={filtro} onChange={e => setFiltro(e.target.value)}>
          <option value="">Tutti i consulenti</option>
          {consulenti.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" className="w-4 h-4" checked={soloProblemi}
                 onChange={e => setSoloProblemi(e.target.checked)} />
          Solo da verificare
        </label>
        {t.sforate > 0 && (
          <span className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
            ■ {t.sforate} {t.sforate === 1 ? 'incarico sforato' : 'incarichi sforati'}
          </span>
        )}
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ['Impegnato', t.impegnato, 'text-gray-900', null],
          ['Fatturato dai consulenti', t.fatturato, 'text-red-600', null],
          // Lavoro svolto e registrato nei rendiconti che non ci è ancora
          // stato fatturato: su commessa a corpo è l'unico avanzamento, perché
          // le ore non passano dalla coda di fatturazione al cliente.
          ['Maturato nei rendiconti', t.maturato, 'text-amber-600',
           'Lavoro già svolto e registrato, non ancora fatturato dai collaboratori'],
          ['Residuo disponibile', t.residuo, t.residuo < 0 ? 'text-red-700' : 'text-green-700', null],
          ['Fatture da pagare', t.da_pagare, 'text-amber-700', null],
        ].map(([l, v, cls, titolo]) => (
          <div key={l} className="bg-gray-50 rounded-lg p-3" title={titolo || undefined}>
            <div className="text-xs text-gray-500">{l}</div>
            <div className={`text-lg font-bold mt-0.5 ${cls}`}>{fmt(v)}</div>
          </div>
        ))}
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-xs text-gray-500 text-left">
              <th className="px-4 py-3">Consulente</th>
              <th className="px-4 py-3">Commessa / Cliente</th>
              <th className="px-4 py-3 text-right">Impegnato</th>
              <th className="px-4 py-3 text-right">Fatturato</th>
              <th className="px-4 py-3 text-right">Residuo</th>
              <th className="px-4 py-3">Consumo</th>
              <th className="px-4 py-3">Verifica</th>
            </tr>
          </thead>
          <tbody>
            {righe.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                {soloProblemi ? 'Nessuno sforamento: tutto sotto controllo' : 'Nessuna offerta consulente accettata'}
              </td></tr>
            )}
            {righe.map(r => {
              const s = SEMAFORO[r.stato_controllo] || SEMAFORO.ok;
              const perc = Math.min(r.percentuale, 100);
              return (
                <tr key={r.id} className={`border-b last:border-0 hover:bg-gray-100 ${s.riga}`}>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {r.consulente}
                    {r.numero && <div className="text-xs font-mono text-gray-400">{r.numero}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {r.commessa_id ? (
                      <>
                        <Link to={`/commesse/${r.commessa_id}`} className="font-mono text-xs text-brand-600 hover:underline">
                          {r.commessa_numero}
                        </Link>
                        <div className="text-xs text-gray-500 truncate max-w-[220px]">{r.cliente_nome || r.commessa_titolo}</div>
                      </>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {fmt(r.importo_max)}
                    <div className="text-[10px] font-normal text-gray-400">
                      {r.modalita_esecuzione === 'economia'
                        ? `in economia${Number(r.ore_previste) > 0 ? ` · ${Number(r.ore_previste)}h` : ''}`
                        : 'a corpo'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {fmt(r.fatturato)}
                    {Number(r.consuntivo) > 0 && (
                      <div className="text-xs text-gray-400">+ {fmt(r.consuntivo)} a consuntivo</div>
                    )}
                    {/* Dai rendiconti: lavoro svolto che deve ancora arrivare
                        in fattura. È qui che si vede l'avanzamento di chi
                        lavora a corpo, dove le ore non vanno al cliente. */}
                    {Number(r.maturato) > 0 && (
                      <div className="text-xs text-amber-600"
                           title={`Dai rendiconti${Number(r.ore_rendicontate) > 0
                             ? `: ${String(Number(r.ore_rendicontate)).replace('.', ',')} ore registrate` : ''}`}>
                        + {fmt(r.maturato)} maturato
                      </div>
                    )}
                  </td>
                  <td className={`px-4 py-3 text-right font-medium ${r.residuo < 0 ? 'text-red-700' : 'text-gray-700'}`}>
                    {fmt(r.residuo)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden w-28">
                      <div className={`h-full rounded-full ${
                        r.stato_controllo === 'sforato' ? 'bg-red-600'
                        : r.percentuale >= 90 ? 'bg-amber-500' : 'bg-brand-600'}`}
                        style={{ width: `${perc}%` }} />
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {r.percentuale}%
                      {/* In economia si può sforare per giornate consumate
                          prima che per importo: si mostra anche quella misura. */}
                      {r.modalita_esecuzione === 'economia' && Number(r.ore_previste) > 0 && (
                        <span className={`ml-1.5 ${r.percentuale_ore > 100 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                          · ore {r.percentuale_ore}%
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-xs font-medium ${s.testo}`}>
                    {s.label ? <>{s.icona} {s.label}</> : <span className="text-green-700">✓ Nei limiti</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400">
        Lo sforamento si misura sulle fatture emesse dal consulente. I consuntivi (quanto ha
        dichiarato di aver svolto) danno un preavviso: se sommati alle fatture superano
        l'impegnato, la riga segnala "Sforamento previsto".
      </p>
    </div>
  );
}
